import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";

export default function ReceivedTransfersPage() {
  const { user } = useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailItems, setDetailItems] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const pharmacyCode = user?.pharmacyCode
    ? String(user.pharmacyCode).replace(/^0+/, "")
    : null;
  const firma = user?.firma || "LEMON";

  const formatExpiration = (value) => {
    if (!value) return "-";
    const clean = String(value).replace(/\s/g, "");
    const d = new Date(clean);

    if (!isNaN(d)) {
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    }

    const match = clean.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) return `${match[3]}.${match[2]}.${match[1]}`;

    return clean;
  };

  useEffect(() => {
    if (!pharmacyCode) {
      if (user) {
        setError("U uživatele chybí kód pobočky.");
        setLoading(false);
      }
      return;
    }

    async function fetchTransfers() {
      setLoading(true);
      try {
        const res = await fetch(
          `/api/transfers/received/${firma}/${pharmacyCode}`,
          { credentials: "include" }
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || "Chyba načítání");
        setData(json || []);
      } catch (err) {
        console.error("❌ Chyba při načítání převodek:", err);
        setError("Nepodařilo se načíst přijaté převodky.");
      } finally {
        setLoading(false);
      }
    }

    fetchTransfers();
  }, [pharmacyCode, firma, user]);

  const loadDetail = async (prevodkaId) => {
    try {
      const res = await fetch(`/api/transfers/detail/${prevodkaId}`, {
        credentials: "include",
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Chyba načítání detailu");

      const items = json || [];
      const defaults = {};
      items.forEach((i) => {
        defaults[i._id] = true; // vše zaškrtnuto (opt-out)
      });

      setCheckedItems(defaults);
      setDetailItems(items);
      setSelectedTransfer(prevodkaId);
      setShowModal(true);
    } catch (err) {
      console.error("❌ Chyba při načítání detailu:", err);
      alert("Nepodařilo se načíst položky převodky.");
    }
  };

  const toggleItem = (id) => {
    setCheckedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const confirmTransfer = async () => {
    if (!selectedTransfer || !pharmacyCode) return;

    const receivedItems = detailItems.map((i) => ({
      _id: i._id,
      status: checkedItems[i._id] ? "přijato" : "nedodáno",
    }));

    const anyReceived = receivedItems.some((i) => i.status === "přijato");
    if (!anyReceived) {
      alert("Nebylo vybráno žádné léčivo k příjmu. Příjem nelze provést.");
      return;
    }

    if (!window.confirm("Opravdu chcete potvrdit příjem této převodky?")) return;

    try {
      const res = await fetch(
        `/api/transfers/receive/${firma}/${pharmacyCode}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            prevodkaId: selectedTransfer,
            receivedItems,
          }),
        }
      );

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Chyba při potvrzení příjmu");

      alert(`Převodka ${selectedTransfer} byla zpracována.`);
      setShowModal(false);
      setSelectedTransfer(null);
      setDetailItems([]);
      setCheckedItems({});
      setData((prev) =>
        prev.filter((t) => t.prevodkaId !== selectedTransfer)
      );
    } catch (err) {
      console.error("❌ Chyba při potvrzení převodky:", err);
      alert("Nepodařilo se potvrdit příjem.");
    }
  };

  if (!user) return <div className="p-4 text-gray-600">Načítám uživatele…</div>;
  if (loading) return <div className="p-4 text-gray-600">Načítám data…</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-6">
        Přijaté převodky – pobočka {pharmacyCode}
      </h1>

      {data.length > 0 ? (
        <table className="min-w-full border-collapse bg-white shadow rounded-lg overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-3 px-4 text-left">Číslo převodky</th>
              <th className="py-3 px-4 text-left">Zdrojová pobočka</th>
              <th className="py-3 px-4 text-left">Stav</th>
              <th className="py-3 px-4 text-left">Akce</th>
            </tr>
          </thead>
          <tbody>
            {data.map((t) => (
              <tr key={t._id} className="border-t hover:bg-gray-50">
                <td className="py-3 px-4">{t.prevodkaId}</td>
                <td className="py-3 px-4">{t.fromPharmacyCode}</td>
                <td className="py-3 px-4 capitalize">{t.stav}</td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => loadDetail(t.prevodkaId)}
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12s-3.75 6.75-9.75 6.75S2.25 12 2.25 12z"
                      />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <span>Detail</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-gray-500">Žádné přijaté převodky.</div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                Příjem převodky {selectedTransfer}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ×
              </button>
            </div>

            <div className="p-4 max-h-[70vh] overflow-y-auto">
              <table className="min-w-full border-collapse bg-white">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="py-2 px-3 text-left">Dodáno</th>
                    <th className="py-2 px-3 text-left">PDK</th>
                    <th className="py-2 px-3 text-left">Název</th>
                    <th className="py-2 px-3 text-left">Množství</th>
                    <th className="py-2 px-3 text-left">NCSD</th>
                    <th className="py-2 px-3 text-left w-40">Exspirace</th>
                    <th className="py-2 px-3 text-left">Šarže</th>
                  </tr>
                </thead>
                <tbody>
                  {detailItems.map((i) => {
                    const delivered = !!checkedItems[i._id];
                    return (
                      <tr
                        key={i._id}
                        className={`border-t ${
                          delivered
                            ? "hover:bg-gray-50"
                            : "bg-red-50 hover:bg-red-100"
                        }`}
                      >
                        <td className="py-2 px-3">
                          <input
                            type="checkbox"
                            checked={delivered}
                            onChange={() => toggleItem(i._id)}
                          />
                        </td>
                        <td className="py-2 px-3">{i.PDK}</td>
                        <td className="py-2 px-3">{i.Název}</td>
                        <td className="py-2 px-3">{i.Množství}</td>
                        <td className="py-2 px-3">
                          {i.NCSD ? i.NCSD.toFixed(2) : "-"}
                        </td>
                        <td className="py-2 px-3 w-40">
                          {formatExpiration(i.Exspirace)}
                        </td>
                        <td className="py-2 px-3">{i.Šarže}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <button
                onClick={confirmTransfer}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
              >
                Potvrdit příjem
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
              >
                Zavřít
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
